import * as d3 from "d3"
import { capitalize } from "../../helpers/string_helpers"
import { parseCoordinate } from "../../helpers/chart_helpers"

export default class LineDrawer {
  static gradientId = 0

  static drawLines({g, lines, dataPoints, hiddenLineKeys = new Set(), ...arg}) {
    const drawers = lines.map((line, index) => new LineDrawer({ line: line, index: index, ...arg}))

    drawers.forEach(drawer => {
      const lineGroup = g
        .append("g")
        .attr("class", "line-series")
        .attr("data-line-key", drawer.lineKey)
        .style("display", hiddenLineKeys.has(drawer.lineKey) ? "none" : null)

      drawer.drawGradient(lineGroup)
      drawer.drawLine(lineGroup)
      if(dataPoints.show) drawer.drawDataPoints(lineGroup, dataPoints.radius)
    })

    return drawers.map(drawer => drawer.lineData)
  }

  static drawLegends({lines, ...arg}) {
    const drawers = lines.map((line, index) => new LineDrawer({ line: line, index: index, ...arg}))
    drawers.forEach(drawer => drawer.addLegend())
  }

  constructor({xAxis = null, yAxis = null, secondaryYAxis = null, options = {}, line, legendsContainer, index}) {
    this.legendContainer = legendsContainer
    this.line = line
    this.index = index
    this.lineOptions = this.line.options
    this.options = options
    this.lineKey = this.line.key
    if(!xAxis && !yAxis) return

    this.xScale = xAxis.scale
    this.xScaleType = xAxis.scaleType

    this.yScale = this.lineOptions.secondary_axis ? secondaryYAxis.scale : yAxis.scale
    this.yScaleType = yAxis.scaleType

    this.lineData = line.data.map(([x, y]) => [
      this.xScale(parseCoordinate(x, this.xScaleType)),
      this.yScale(parseCoordinate(y, this.yScaleType))
    ])

    this.curve = d3[`curve${this.lineOptions.curve.split("_").map(capitalize).join("")}`] ?? d3.curveLinear
  }

  drawGradient(g) {
    if (!this.lineOptions.gradient) return

    const gradientId = `line-gradient-${LineDrawer.gradientId++}`
    const svg = d3.select(g.node().ownerSVGElement)

    let defs = svg.select("defs.line-chart-definitions")

    if (defs.empty()) {
      defs = svg
        .append("defs")
        .attr("class", "line-chart-definitions")
    }

    const gradient = defs
      .append("linearGradient")
      .attr("id", gradientId)
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%")

    gradient
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", this.lineOptions.color)
      .attr(
        "stop-opacity",
        this.lineOptions.gradient_opacity ?? 0.2
      )

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", this.lineOptions.color)
      .attr("stop-opacity", 0)

    const baseline = d3.max(this.yScale.range())

    const areaGenerator = d3
      .area()
      .x(([x]) => x)
      .y0(baseline)
      .y1(([, y]) => y)
      .curve(this.curve)

    g.append("path")
      .datum(this.lineData)
      .attr("d", areaGenerator)
      .attr("fill", `url(#${gradientId})`)
      .attr("stroke", "none")
      .attr("pointer-events", "none")
  }

  drawLine(g) {
    const lineGenerator = d3
      .line()
      .x(([x]) => x)
      .y(([, y]) => y)
      .curve(this.curve)

    g.append("path")
      .datum(this.lineData)
      .attr("d", lineGenerator)
      .attr("fill", "none")
      .attr("stroke", this.lineOptions.color)
      .attr("stroke-width", this.lineOptions.stroke_width)
      .attr("stroke-dasharray", this.lineOptions.dash_pattern || null)
  }

  drawDataPoints(g, radius) {
    const pointsGroup = g.append("g").attr("class", "line-data-points")

    pointsGroup.selectAll(".line-data-point")
        .data(this.lineData)
        .join("circle")
        .attr("class", "line-data-point")
        .attr("cx", ([x]) => x)
        .attr("cy", ([, y]) => y)
        .attr("r", radius)
        .attr("fill", this.lineOptions.color)
  }

  addLegend() {
    const template = document.createElement("template")
    template.innerHTML = this._legend(this.lineOptions.color, this.lineKey)
    const legend = template.content.firstElementChild
    legend.querySelector(".legend-name").textContent = this.lineOptions.label
    this.legendContainer.append(legend)
  }

  _legend(color, lineKey) {
    return `
        <div class="line-chart-legend" data-line-key="${lineKey}" style="cursor:pointer;">
            <div class="legend-details">
                <div class="legend-row">
                    <div class="legend-color" style="background-color:${color};"></div>
                    <div class="legend-name"></div>
                </div>
                <div class="legend-row">
                    <div class="legend-color legend-transparent"></div>
                    <div class="legend-values" data-index="${this.index}">
                        <div class="legend-data">--</div>
                        ${ this.options.show_percentage ? '<div class="legend-percentage">--%</div>' : '' }
                    </div>
                </div>
            </div>
        </div>
    `
  }
}
